import React, { useState } from "react";
import { Link } from "react-router-dom";
import { signInErrorSummary } from "../utils/signInErrors";

/**
 * Inline, persistent explanation of a failed sign-in (a toast disappears
 * before anyone can read a reference id off it). Shows the title, the
 * message, an optional next step, and a collapsed "Technical details" block
 * with the code / HTTP status / request id / endpoint - what support needs
 * from a screenshot, hidden from people who don't.
 *
 * `error` is the object returned by describeSignInError().
 */
function SignInErrorAlert({ error, onRetry, className = "" }) {
    const [copied, setCopied] = useState(false);
    if (!error) return null;

    const copySummary = async () => {
        try {
            await navigator.clipboard.writeText(signInErrorSummary(error));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard can be unavailable (http origin, permissions); the
            // text is on screen anyway.
        }
    };

    const rows = [
        ["Code", error.code || "—"],
        ["HTTP status", error.status || "no response"],
        ["Reference", error.requestId || "—"],
        ["Endpoint", error.url || "—"],
        ["Time", error.at?.toLocaleString?.() || "—"],
    ];
    if (error.details && Object.keys(error.details).length) {
        rows.push(["Details", JSON.stringify(error.details)]);
    }

    return (
        <div
            className={`alert alert-danger py-2 px-3 mb-3 text-left ${className}`}
            role="alert"
            style={{ fontSize: "14px" }}
        >
            <div className="font-weight-bold">{error.title}</div>
            <div>{error.message}</div>

            {(error.action || (error.transient && onRetry)) && (
                <div className="mt-2">
                    {error.action && (
                        <Link to={error.action.to} className="alert-link">
                            {error.action.label}
                        </Link>
                    )}
                    {error.action && error.transient && onRetry && " · "}
                    {error.transient && onRetry && (
                        <button
                            type="button"
                            className="btn btn-link alert-link p-0 align-baseline"
                            style={{ fontSize: "14px" }}
                            onClick={onRetry}
                        >
                            Try again
                        </button>
                    )}
                </div>
            )}

            <details className="mt-2" style={{ fontSize: "12px" }}>
                <summary style={{ cursor: "pointer" }}>
                    Technical details
                </summary>
                <table className="mb-1 mt-1" style={{ width: "100%" }}>
                    <tbody>
                        {rows.map(([label, value]) => (
                            <tr key={label}>
                                <td
                                    className="pr-2 text-muted align-top"
                                    style={{ whiteSpace: "nowrap" }}
                                >
                                    {label}
                                </td>
                                <td
                                    className="align-top"
                                    style={{ wordBreak: "break-all" }}
                                >
                                    {String(value)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <button
                    type="button"
                    className="btn btn-link p-0 alert-link"
                    style={{ fontSize: "12px" }}
                    onClick={copySummary}
                >
                    {copied ? "Copied" : "Copy for support"}
                </button>
            </details>
        </div>
    );
}

export default SignInErrorAlert;
